import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider.js";
import { BrandLogo } from "../ui/BrandLogo.js";
import { Field, inputClass } from "./SignIn.js";

export function ConfirmSignUp() {
  const { confirmSignUp, resendCode } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [email, setEmail] = useState(params.get("email") ?? "");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      await confirmSignUp({ email, code });
      navigate("/sign-in", { replace: true, state: { from: "/portal" } });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Confirmation failed");
    } finally {
      setSubmitting(false);
    }
  }

  async function onResend() {
    setError(null);
    setInfo(null);
    try {
      await resendCode({ email });
      setInfo("Sent a new code. Check your inbox.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not resend code");
    }
  }

  return (
    <main className="mx-auto max-w-md px-6 py-10">
      <Link to="/sign-in" className="text-sm text-brand-700">
        &larr; Back to sign in
      </Link>
      <BrandLogo className="mx-auto mt-6" />
      <h1 className="mt-4 text-center text-2xl font-bold text-charcoal">Verify your email</h1>
      <p className="mt-2 text-center text-sm text-charcoal">
        Enter the 6-digit code we just emailed to {email || "your inbox"}.
      </p>

      <form className="mt-8 space-y-5" onSubmit={onSubmit} noValidate>
        <Field label="Email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            required
            className={inputClass}
          />
        </Field>
        <Field label="Verification code">
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={6}
            required
            className={inputClass + " tracking-[0.4em]"}
          />
        </Field>

        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        {info && <p className="text-sm text-brand-700">{info}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-brand-700 px-6 text-base font-semibold text-white shadow-sm active:bg-brand-800 disabled:opacity-60"
        >
          {submitting ? "Verifying…" : "Verify"}
        </button>
        <button
          type="button"
          onClick={onResend}
          className="block w-full min-h-[44px] text-sm font-medium text-brand-700"
        >
          Resend code
        </button>
      </form>
    </main>
  );
}
